import {useMutation} from "@tanstack/react-query"
import {Typography} from "antd"
import moment from "moment"
import {BaseButton, BaseInput} from "components"
import {handleError, toastSuccess} from "helpers"
import {useState} from "react"
import {useTranslation} from "react-i18next"
import {AuthNetEnvironment, DispatchDataResponse, useAcceptJs} from "react-acceptjs"
import {usePaymentProviderConfigs} from "hooks/usePaymentProviderConfigs"
import {studentPaymentService} from "services"
import {
    PaymentProvider,
    RecurringPaymentInterval,
    StudentPaymentSupportedCurrency
} from "types/student-account/student-payment"
import styles from "./RecurringPayments.module.css"
import {FormLabel} from "components/Form"

type Props = {
    profileId: number
    amountInCents: number
    currency: StudentPaymentSupportedCurrency
    interval: RecurringPaymentInterval
    intervalCount?: number
    cancelAt: moment.Moment | null
    onSuccess: () => void
}

export const AuthorizeNetRecurringSubscriptionForm = ({
    profileId,
    amountInCents,
    currency,
    interval,
    intervalCount,
    cancelAt,
    onSuccess
}: Props) => {
    const {t} = useTranslation(["payment", "common"])
    const {paymentProviderConfigs} = usePaymentProviderConfigs(profileId)
    const providerConfig = paymentProviderConfigs.find((c) => c.provider === PaymentProvider.AuthorizeNet) as any

    const authData = {
        apiLoginID: providerConfig?.loginId ?? "",
        clientKey: providerConfig?.clientKey ?? ""
    }

    const {dispatchData, loading: acceptJsLoading} = useAcceptJs({
        environment: ((providerConfig?.paymentEnvironment ?? "SANDBOX") as string).toUpperCase() as AuthNetEnvironment,
        authData
    })

    const [cardNumber, setCardNumber] = useState("")
    const [expiry, setExpiry] = useState("")
    const [cardCode, setCardCode] = useState("")
    const [cardholderName, setCardholderName] = useState("")

    const {isLoading, mutate: submit} = useMutation({
        mutationFn: async () => {
            if (!cardholderName.trim()) throw new Error(t("common:validation.cantEmpty", {field: "Cardholder Name"}))
            if (!cardNumber.trim()) throw new Error(t("common:validation.cantEmpty", {field: "Card Number"}))
            if (!expiry.trim()) throw new Error(t("common:validation.cantEmpty", {field: "Expiry"}))
            if (!cardCode.trim()) throw new Error(t("common:validation.cantEmpty", {field: "Card Code"}))

            const expiryParts = expiry.split("/")
            const response: DispatchDataResponse = await dispatchData({
                cardData: {
                    fullName: cardholderName,
                    cardNumber: cardNumber.replace(/\s/g, ""),
                    month: expiryParts[0] ?? "",
                    year: expiryParts[1] ?? "",
                    cardCode,
                    zip: ""
                }
            })

            if (!response?.opaqueData) {
                throw new Error(t("msg.recurringPayment.failedToCreate"))
            }

            return studentPaymentService.createAuthorizeNetRecurringSubscription({
                profileId,
                amountInCents,
                currency,
                interval,
                intervalCount,
                cancelAt: cancelAt?.toISOString() ?? null,
                authorizeNetPaymentNonce: {opaqueData: response.opaqueData}
            })
        },
        onError: (error: any) => {
            const acceptJsText = error?.messages?.message?.[0]?.text
            handleError(acceptJsText ? new Error(acceptJsText) : error)
        },
        onSuccess: () => {
            toastSuccess(t("msg.recurringPayment.addedSuccessfully"))
            onSuccess()
        }
    })

    if (amountInCents <= 0) {
        return (
            <Typography.Paragraph type="secondary">
                {t("msg.recurringPayment.enterAmount", "Please enter an amount greater than 0")}
            </Typography.Paragraph>
        )
    }

    return (
        <div>
            <div className={styles.payFields}>
                <FormLabel label="Cardholder Name" />
                <BaseInput value={cardholderName} onChange={setCardholderName} placeholder="John Doe" />
            </div>
            <div className={styles.payFields}>
                <FormLabel label="Card Number" />
                <BaseInput
                    value={cardNumber}
                    onChange={(value) => {
                        const cleaned = value.replace(/\s/g, "")
                        const formatted = cleaned.match(/.{1,4}/g)?.join(" ") ?? cleaned
                        setCardNumber(formatted)
                    }}
                    placeholder="xxxx xxxx xxxx xxxx"
                    maxLength={23}
                />
            </div>
            <div className={styles.payFields}>
                <FormLabel label="Expiry" />
                <BaseInput
                    value={expiry}
                    onChange={(value) => {
                        const formatted =
                            value.trim().length > 2 ? value.replace(/\//g, "").replace(/^(.{2})/, "$1/") : value
                        setExpiry(formatted)
                    }}
                    placeholder="mm/yyyy"
                    maxLength={7}
                />
            </div>
            <div className={styles.payFields}>
                <FormLabel label="Card Code" />
                <BaseInput value={cardCode} onChange={setCardCode} placeholder="xxx" maxLength={4} />
            </div>
            <BaseButton
                size="small"
                title={t("common:action.add")}
                className="w-full mt-24"
                loading={isLoading || acceptJsLoading}
                onClick={() => submit()}
            />
        </div>
    )
}
